// Funções utilitárias para manipulação de configurações

export const TIPOS_ENTRADA_TEMPLATE = [
  'text',
  'number',
  'boolean',
  'color',
  'image',
  'html',
  'object',
  'array',
  'multitable',
];

/**
 * Normaliza o tipo de uma entrada de template.
 */
export function normalizarTipoEntrada(tipo) {
  const valor = String(tipo || '').trim();
  if (!valor) return 'text';

  return valor;
}

/**
 * Normaliza campos de uma entrada de template vindos da API.
 */
export function normalizarEntradaTemplate(entrada) {
  if (!entrada || typeof entrada !== 'object') return entrada;

  return {
    ...entrada,
    tipo: normalizarTipoEntrada(entrada.tipo),
  };
}

/**
 * Extrai todas as entradas de uma árvore de configurações.
 */
export function extractEntriesFromTree(tree) {
  const entries = [];
  const traverse = (node) => {
    if (node.__entradas && Array.isArray(node.__entradas)) {
      entries.push(...node.__entradas);
    }
    if (node.__children) {
      Object.values(node.__children).forEach(child => {
        traverse(child);
      });
    }
  };
  Object.values(tree).forEach(node => {
    traverse(node);
  });
  return entries;
}

/**
 * Ordena as entradas da configuração conforme a ordem definida no template.
 */
export function ordenarCurrentConfig(currentConfig, obterOrdem) {
  currentConfig.entradas.forEach((entrada) => {
    entrada.ordem = obterOrdem(entrada.chave);
  });
  currentConfig.entradas.sort((entradaA, entradaB) => entradaA.ordem - entradaB.ordem);
}

/**
 * Agrupa as entradas do template por grupo e subgrupo.
 */
export function getGroupedEntradas(currentTemplate) {
  if (!currentTemplate) return {};
  const grouped = {};
  currentTemplate.entradas.forEach((entrada) => {
    const grupo = entrada.agrupamento?.nome?.toUpperCase() || 'SEM GRUPO';
    const subgrupo = entrada.agrupamento?.subgrupo?.nome?.toUpperCase() || '';
    if (!grouped[grupo]) grouped[grupo] = {};
    if (!grouped[grupo][subgrupo]) grouped[grupo][subgrupo] = [];
    grouped[grupo][subgrupo].push(entrada);
  });
  const orderedGrouped = {};
  const sortedKeys = Object.keys(grouped).sort((a, b) => {
    if (a === 'GERAL') return -1;
    if (b === 'GERAL') return 1;
    return a.localeCompare(b);
  });
  sortedKeys.forEach((key) => {
    const subgrupos = grouped[key];
    const orderedSub = {};
    const subKeys = Object.keys(subgrupos).sort((a, b) => {
      if (a === '') return -1;
      if (b === '') return 1;
      return a.localeCompare(b);
    });
    subKeys.forEach((subKey) => {
      orderedSub[subKey] = subgrupos[subKey];
    });
    orderedGrouped[key] = orderedSub;
  });
  return orderedGrouped;
}

/**
 * Constrói uma árvore de entradas a partir de uma lista de entradas do template e valores da configuração.
 */
export function buildTreeFromEntradas(entradas, currentConfig) {
  const tree = {};
  const configValues = {};
  currentConfig.entradas.forEach(entrada => {
    configValues[entrada.chave] = entrada.valor;
  });
  entradas.forEach((entrada) => {
    const entradaWithValue = {
      ...entrada,
      valor: configValues[entrada.chave] || entrada.valorPadrao,
      collapse: true
    };
    let node = tree;
    let grupoAtual = entrada.agrupamento;
    while (grupoAtual) {
      const nomeGrupo = normalizarNomeAba(grupoAtual.nome);
      if (!node[nomeGrupo]) {
        node[nomeGrupo] = {
          __children: {},
          __entradas: [],
          collapse: true
        };
      }
      if (grupoAtual.subgrupo && grupoAtual.subgrupo.nome) {
        node = node[nomeGrupo].__children;
        grupoAtual = grupoAtual.subgrupo;
      } else {
        node[nomeGrupo].__entradas.push(entradaWithValue);
        break;
      }
    }
    if (!entrada.agrupamento) {
      if (!tree['GERAL']) {
        tree['GERAL'] = {
          __children: {},
          __entradas: [],
          collapse: true
        };
      }
      tree['GERAL'].__entradas.push(entradaWithValue);
    }
  });
  return tree;
}

/**
 * Garante que um caminho de agrupamento exista na árvore (aba vazia).
 */
export function injetarCaminhoNaArvore(tree, caminho = []) {
  if (!caminho.length) return tree;

  let node = tree;
  caminho.forEach((parte, indice) => {
    const nome = normalizarNomeAba(parte);
    if (!node[nome]) {
      node[nome] = {
        __children: {},
        __entradas: [],
        collapse: true,
      };
    }

    if (indice < caminho.length - 1) {
      node = node[nome].__children;
    }
  });

  return tree;
}

/**
 * Lista todos os caminhos de abas presentes na árvore.
 */
export function listarCaminhosNaArvore(tree, caminhoPai = []) {
  const caminhos = [];

  Object.keys(tree || {}).forEach((nome) => {
    const caminho = [...caminhoPai, nome];
    caminhos.push(caminho);

    const filhos = tree[nome]?.__children;
    if (filhos && Object.keys(filhos).length) {
      caminhos.push(...listarCaminhosNaArvore(filhos, caminho));
    }
  });

  return caminhos;
}

/**
 * Monta árvore do template incluindo abas vazias criadas na interface.
 */
export function buildTreeFromTemplateEntradasComExtras(entradas = [], caminhosExtras = []) {
  const tree = buildTreeFromTemplateEntradas(entradas);

  caminhosExtras.forEach((caminho) => {
    injetarCaminhoNaArvore(tree, caminho);
  });

  return tree;
}

/**
 * Compara posição de abas com base na menor ordem das entradas do grupo.
 */
function compararPosicaoAbas(abaA, abaB) {
  const [nomeA, ordemA] = abaA;
  const [nomeB, ordemB] = abaB;
  const normA = normalizarNomeAba(nomeA);
  const normB = normalizarNomeAba(nomeB);

  if (normA === 'GERAL') return -1;
  if (normB === 'GERAL') return 1;
  if (ordemA !== ordemB) return ordemA - ordemB;
  return normA.localeCompare(normB);
}

/**
 * Calcula a ordem fixa das abas em cada nível a partir das entradas.
 */
export function calcularOrdemAbasPorEntradas(entradas = []) {
  const niveis = new Map();

  const registrar = (caminhoPai, nomeAba, ordem) => {
    const chaveNivel = caminhoPai.map(normalizarNomeAba).join('/');
    if (!niveis.has(chaveNivel)) niveis.set(chaveNivel, new Map());

    const mapa = niveis.get(chaveNivel);
    const nome = normalizarNomeAba(nomeAba);
    const ordemAtual = mapa.get(nome);

    if (ordemAtual === undefined || ordem < ordemAtual) {
      mapa.set(nome, ordem);
    }
  };

  entradas.forEach((entrada) => {
    const caminho = extrairCaminhoAgrupamento(entrada.agrupamento);

    if (!caminho.length) {
      registrar([], 'GERAL', entrada.ordem);
      return;
    }

    caminho.forEach((nomeAba, indice) => {
      registrar(caminho.slice(0, indice), nomeAba, entrada.ordem);
    });
  });

  const resultado = {};
  niveis.forEach((mapa, chaveNivel) => {
    resultado[chaveNivel] = [...mapa.entries()]
      .sort(compararPosicaoAbas)
      .map(([nome]) => nome);
  });

  return resultado;
}

/**
 * Ordena chaves de abas irmãs respeitando ordem fixa conhecida.
 */
export function ordenarChavesNivel(chaves = [], ordemFixa = []) {
  const ordemNormalizada = (ordemFixa || []).map(normalizarNomeAba);

  return [...chaves].sort((chaveA, chaveB) => {
    const normA = normalizarNomeAba(chaveA);
    const normB = normalizarNomeAba(chaveB);
    const posA = ordemNormalizada.indexOf(normA);
    const posB = ordemNormalizada.indexOf(normB);
    const indiceA = posA === -1 ? Number.MAX_SAFE_INTEGER : posA;
    const indiceB = posB === -1 ? Number.MAX_SAFE_INTEGER : posB;

    if (indiceA !== indiceB) return indiceA - indiceB;
    return normA.localeCompare(normB);
  });
}

/**
 * Reordena a árvore de abas sem alterar a ordem definida em cada nível.
 */
export function reordenarArvore(tree, ordemAbasPorNivel = {}, caminhoPai = []) {
  if (!tree || !Object.keys(tree).length) return tree;

  const chaveNivel = caminhoPai.map(normalizarNomeAba).join('/');
  const chavesOrdenadas = ordenarChavesNivel(Object.keys(tree), ordemAbasPorNivel[chaveNivel]);
  const resultado = {};

  chavesOrdenadas.forEach((nome) => {
    const no = tree[nome];
    const caminho = [...caminhoPai, nome];
    const filhos = no.__children && Object.keys(no.__children).length
      ? reordenarArvore(no.__children, ordemAbasPorNivel, caminho)
      : no.__children;

    resultado[nome] = filhos !== no.__children
      ? { ...no, __children: filhos }
      : no;
  });

  return resultado;
}

/**
 * Extrai o caminho de agrupamento de uma entrada.
 */
export function extrairCaminhoAgrupamento(agrupamento) {
  const caminho = [];
  let atual = agrupamento;
  while (atual && atual.nome) {
    caminho.push(normalizarNomeAba(atual.nome));
    atual = atual.subgrupo;
  }
  return caminho;
}

/**
 * Formata agrupamento para exibição legível.
 */
export function formatarAgrupamento(agrupamento) {
  const caminho = extrairCaminhoAgrupamento(agrupamento);
  return caminho.length ? caminho.join(' → ') : 'Raiz';
}

/**
 * Monta catálogo de grupos e subgrupos usados nas entradas do template.
 */
export function extrairCatalogoAgrupamento(entradas = []) {
  const grupos = new Set(['GERAL']);
  const subgrupos = {};

  const registrar = (agrupamento) => {
    if (!agrupamento?.nome) return;

    const grupo = agrupamento.nome.toUpperCase();
    grupos.add(grupo);

    const sub = agrupamento.subgrupo?.nome?.toUpperCase();
    if (sub) {
      if (!subgrupos[grupo]) subgrupos[grupo] = new Set();
      subgrupos[grupo].add(sub);
    }
  };

  entradas.forEach((entrada) => registrar(entrada.agrupamento));

  const gruposOrdenados = [...grupos].sort((a, b) => {
    if (a === 'GERAL') return -1;
    if (b === 'GERAL') return 1;
    return a.localeCompare(b);
  });

  const subgruposOrdenados = {};
  Object.keys(subgrupos).forEach((grupo) => {
    subgruposOrdenados[grupo] = [...subgrupos[grupo]].sort();
  });

  return { grupos: gruposOrdenados, subgrupos: subgruposOrdenados };
}

/**
 * Normaliza nome de aba/grupo para o padrão usado nas entradas.
 */
export function normalizarNomeAba(nome) {
  const valor = String(nome || '').toUpperCase();
  if (!valor || valor === 'SEM GRUPO') return 'GERAL';
  return valor;
}

/**
 * Monta agrupamento a partir de um caminho hierárquico.
 */
export function agrupamentoFromCaminho(caminho = []) {
  if (!caminho.length) {
    return { nome: 'GERAL', subgrupo: {} };
  }

  const nomes = caminho.map(parte => normalizarNomeAba(parte));
  const raiz = { nome: nomes[0], subgrupo: {} };
  let atual = raiz;

  for (let i = 1; i < nomes.length; i += 1) {
    atual.subgrupo = { nome: nomes[i], subgrupo: {} };
    atual = atual.subgrupo;
  }

  return raiz;
}

/**
 * Chave estável para identificar a seção de agrupamento de uma entrada.
 */
export function chaveSecaoAgrupamento(agrupamento) {
  const caminho = extrairCaminhoAgrupamento(agrupamento);
  return caminho.length ? caminho.join('/') : 'GERAL';
}

/**
 * Agrupa entradas em seções para reorganização visual.
 */
export function buildSecoesReorganizar(entradas = []) {
  const mapa = new Map();

  [...entradas]
    .sort((a, b) => a.ordem - b.ordem)
    .forEach((entrada) => {
      const caminho = extrairCaminhoAgrupamento(entrada.agrupamento);
      const chave = chaveSecaoAgrupamento(entrada.agrupamento);

      if (!mapa.has(chave)) {
        mapa.set(chave, {
          chave,
          caminho: caminho.length ? caminho : ['GERAL'],
          label: formatarAgrupamento(entrada.agrupamento),
          entradas: [],
        });
      }

      mapa.get(chave).entradas.push(entrada);
    });

  return [...mapa.values()].sort((secaoA, secaoB) => {
    const ordemA = Math.min(...secaoA.entradas.map(entrada => entrada.ordem));
    const ordemB = Math.min(...secaoB.entradas.map(entrada => entrada.ordem));
    return ordemA - ordemB;
  });
}

/**
 * Recalcula a ordem global das entradas com base na ordem das seções.
 */
export function recalcularOrdemEntradasPorSecoes(secoes = []) {
  let ordem = 0;

  secoes.forEach((secao) => {
    secao.entradas.forEach((entrada) => {
      entrada.ordem = ordem;
      ordem += 1;
    });
  });
}

/**
 * Constrói árvore de abas para visualização semelhante à edição de configuração.
 */
export function buildTreeFromTemplateEntradas(entradas = []) {
  const mockConfig = {
    entradas: entradas.map(entrada => ({
      chave: entrada.chave,
      valor: entrada.valorPadrao,
    })),
  };

  return buildTreeFromEntradas(
    [...entradas].sort((a, b) => a.ordem - b.ordem),
    mockConfig
  );
}

/**
 * Busca o tipo de uma entrada pelo template.
 */
export function obterTipo(currentTemplate, chave) {
  if (currentTemplate && currentTemplate.entradas.find((el) => el.chave == chave)) {
    return currentTemplate.entradas.find((el) => el.chave == chave).tipo;
  } else {
    return null;
  }
}

/**
 * Busca a descrição de uma entrada pelo template.
 */
export function obterDescricao(currentTemplate, chave) {
  if (currentTemplate && currentTemplate.entradas.find((el) => el.chave == chave)) {
    return currentTemplate.entradas.find((el) => el.chave == chave).descricao;
  } else {
    return null;
  }
}

/**
 * Busca o valor padrão de uma entrada pelo template.
 */
export function obterValorPadrao(currentTemplate, chave) {
  if (currentTemplate && currentTemplate.entradas.find((el) => el.chave == chave)) {
    return currentTemplate.entradas.find((el) => el.chave == chave).valorPadrao;
  } else {
    return null;
  }
}

/**
 * Busca a ordem de uma entrada pelo template.
 */
export function obterOrdem(currentTemplate, chave) {
  if (currentTemplate && currentTemplate.entradas.find((el) => el.chave == chave)) {
    return currentTemplate.entradas.find((el) => el.chave == chave).ordem;
  } else {
    return null;
  }
}

/**
 * Busca o valor de uma configuração pela chave.
 */
export function getValorConfig(currentConfig, chave) {
  const entrada = currentConfig.entradas.find((e) => e.chave === chave);
  return entrada?.valor || '';
}

/**
 * Substitui valores em massa nas entradas de configuração.
 */
export function atualizarEntradas(currentConfig, textoAntigo, textoNovo, obterTipo) {
  let contador = 0;
  
  // Validar parâmetros
  if (!textoAntigo || !textoNovo || !currentConfig || !currentConfig.entradas) {
    return contador;
  }
  
  // Normalizar textos para comparação
  const textoAntigoLower = textoAntigo.toLowerCase();
  const textoNovoLower = textoNovo.toLowerCase();
  
  // Criar variações do texto antigo para busca
  const variacoesTextoAntigo = [
    textoAntigo, // Original
    textoAntigoLower, // Minúsculo
    textoAntigo.toUpperCase(), // Maiúsculo
    textoAntigo.charAt(0).toUpperCase() + textoAntigo.slice(1).toLowerCase(), // Primeira letra maiúscula
  ];
  
  // Criar variações do texto novo para substituição
  const variacoesTextoNovo = [
    textoNovo, // Original
    textoNovoLower, // Minúsculo
    textoNovo.toUpperCase(), // Maiúsculo
    textoNovo.charAt(0).toUpperCase() + textoNovo.slice(1).toLowerCase(), // Primeira letra maiúscula
  ];
  
  currentConfig.entradas.forEach((entrada) => {
    // Verificar se a entrada tem valor
    if (!entrada.valor) {
      return;
    }
    
    const tipoDaEntrada = obterTipo ? obterTipo(entrada.chave) : 'string';
    
    let valorOriginal = entrada.valor;
    let valorModificado = entrada.valor;
    let houveModificacao = false;
    
    // Função auxiliar para fazer substituição em string
    const fazerSubstituicao = (texto) => {
      let resultado = texto;
      let modificado = false;
      
      // Tentar cada variação do texto antigo
      for (let i = 0; i < variacoesTextoAntigo.length; i++) {
        const textoAntigoVariacao = variacoesTextoAntigo[i];
        const textoNovoVariacao = variacoesTextoNovo[i];
        
        if (resultado.includes(textoAntigoVariacao)) {
          resultado = resultado.replace(
            new RegExp(textoAntigoVariacao.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
            textoNovoVariacao
          );
          modificado = true;
        }
      }
      
      return { resultado, modificado };
    };
    
        // Tratar diferentes tipos de dados
    // Primeiro verificar se é um array (mesmo que o tipo seja 'object')
    let dados = entrada.valor;
    let dadosParseados = false;
    
    // Se for string, tentar fazer parse
    if (typeof dados === 'string') {
      try {
        dados = JSON.parse(dados);
        dadosParseados = true;
      } catch (error) {
        // Silenciar erro de parse
      }
    }
    
    // Verificar se é um array (independente do tipo declarado)
    if (Array.isArray(dados)) {
      let arrayModificado = false;
      let substituicoesNoArray = 0;
      
      const novoArray = dados.map((item, index) => {
        if (typeof item === 'object' && item !== null) {
          // Para objetos, substituir apenas nos valores, não nas chaves
          const novoItem = {};
          let itemModificado = false;
          
          Object.keys(item).forEach(chave => {
            const valor = item[chave];
            
            if (typeof valor === 'string') {
              const { resultado, modificado } = fazerSubstituicao(valor);
              novoItem[chave] = resultado;
              if (modificado) {
                itemModificado = true;
                substituicoesNoArray++;
              } else {
                novoItem[chave] = valor;
              }
            } else {
              novoItem[chave] = valor;
            }
          });
          
          if (itemModificado) {
            arrayModificado = true;
          }
          
          return novoItem;
        } else if (typeof item === 'string') {
          // Para strings simples no array
          const { resultado, modificado } = fazerSubstituicao(item);
          if (modificado) {
            arrayModificado = true;
            substituicoesNoArray++;
          }
          return resultado;
        } else {
          return item;
        }
      });
      
      if (arrayModificado) {
        valorModificado = JSON.stringify(novoArray);
        houveModificacao = true;
      }
    } else if (tipoDaEntrada === 'table' || tipoDaEntrada === 'multitable' || tipoDaEntrada === 'array' || tipoDaEntrada === 'list') {
      // Se não é array mas o tipo é de tabela, tentar tratar como string
      const { resultado, modificado } = fazerSubstituicao(entrada.valor);
      if (modificado) {
        valorModificado = resultado;
        houveModificacao = true;
      }
    } else if (tipoDaEntrada === 'object' || tipoDaEntrada === 'json') {
      // Para objetos JSON
      try {
        let dados = entrada.valor;
        
        // Se for string, tentar fazer parse
        if (typeof dados === 'string') {
          dados = JSON.parse(dados);
        }
        
        // Verificar se é um objeto
        if (typeof dados === 'object' && dados !== null && !Array.isArray(dados)) {
          const novoObjeto = {};
          let objetoModificado = false;
          let substituicoesNoObjeto = 0;
          
          Object.keys(dados).forEach(chave => {
            const valor = dados[chave];
            if (typeof valor === 'string') {
              const { resultado, modificado } = fazerSubstituicao(valor);
              novoObjeto[chave] = resultado;
              if (modificado) {
                objetoModificado = true;
                substituicoesNoObjeto++;
              }
            } else {
              novoObjeto[chave] = valor;
            }
          });
          
          if (objetoModificado) {
            valorModificado = JSON.stringify(novoObjeto);
            houveModificacao = true;
          }
        }
                      } catch (error) {
          // Se falhar o parse, tratar como string normal
          const { resultado, modificado } = fazerSubstituicao(entrada.valor);
          if (modificado) {
            valorModificado = resultado;
            houveModificacao = true;
          }
        }
    } else {
      // Para strings e outros tipos, usar a lógica original
      if (typeof entrada.valor === 'string') {
        const { resultado, modificado } = fazerSubstituicao(entrada.valor);
        if (modificado) {
          valorModificado = resultado;
          houveModificacao = true;
        }
      }
    }
    
    // Se houve modificação, atualizar o valor da entrada
    if (houveModificacao && valorModificado !== valorOriginal) {
      entrada.valor = valorModificado;
      contador++;
    }
  });
  
  return contador;
}

 